import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import classnames from 'classnames'

import './styles.css'

const NON_DIGITS = /\D/g
const NON_ALPHANUMERIC = /[^0-9a-z]/gi

// Per-mode sanitising and keyboard hints. Numeric is the OTP default (digits,
// numeric keypad, OS one-time-code autofill); alphanumeric takes certificate
// numbers (letters + digits, uppercased, no autofill or autocorrect).
const MODES = {
  numeric: {
    clean: (raw) => raw.replace(NON_DIGITS, ''),
    inputProps: {
      inputMode: 'numeric',
      pattern: '[0-9]*',
      autoComplete: 'one-time-code'
    }
  },
  alphanumeric: {
    clean: (raw) => raw.replace(NON_ALPHANUMERIC, '').toUpperCase(),
    inputProps: {
      inputMode: 'text',
      autoComplete: 'off',
      autoCapitalize: 'characters',
      autoCorrect: 'off',
      spellCheck: false
    }
  }
}

/**
 * Verification code input: one real <input> takes the keystrokes, the paste,
 * and the OS one-time-code autofill, while a row of cells echoes its value
 * one character each. The input sits invisibly over the cells so tapping any
 * of them focuses it; its caret is kept at the end so typing always lands in
 * the next empty cell. Characters outside the mode's alphabet are dropped and
 * the value is capped at `length`, which is also when `onComplete` fires.
 * `groupSize` draws a visual separator between groups of cells (the printed
 * `A1B2-C3D4` form); it is decoration only and never part of the value.
 */
export function CodeInput({
  id,
  length = 6,
  mode = 'numeric',
  groupSize = 0,
  value,
  onChange,
  onComplete,
  invalid = false,
  disabled = false,
  autoFocus = false,
  describedBy
}) {
  const inputRef = useRef(null)
  const [focused, setFocused] = useState(false)
  const { clean, inputProps } = MODES[mode]

  const handleChange = (event) => {
    const characters = clean(event.target.value).slice(0, length)
    onChange(characters)
    if (characters.length === length && characters !== value && onComplete)
      onComplete(characters)
  }

  // A click can drop the caret mid-string; typing must append, not insert.
  // Keyboard selection (select-all to replace) is left alone.
  const keepCaretAtEnd = () => {
    const input = inputRef.current
    if (!input) return
    const end = input.value.length
    if (input.selectionStart !== end || input.selectionEnd !== end)
      input.setSelectionRange(end, end)
  }

  const activeIndex = Math.min(value.length, length - 1)
  const separatorAfter = (index) =>
    groupSize > 0 && (index + 1) % groupSize === 0 && index < length - 1

  return (
    <div
      className={classnames('code-input', {
        '-error': invalid,
        '-disabled': disabled,
        '-focused': focused,
        '-long': length > 6
      })}
    >
      <input
        ref={inputRef}
        id={id}
        className="code-input__field"
        type="text"
        {...inputProps}
        value={value}
        onChange={handleChange}
        onFocus={() => {
          setFocused(true)
          keepCaretAtEnd()
        }}
        onBlur={() => setFocused(false)}
        onMouseUp={keepCaretAtEnd}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      <div className="code-input__cells" aria-hidden="true">
        {Array.from({ length }, (_, index) => (
          <React.Fragment key={index}>
            <span
              className={classnames('code-input__cell', {
                '-filled': Boolean(value[index]),
                '-active': focused && index === activeIndex
              })}
            >
              {value[index] || ''}
            </span>
            {separatorAfter(index) && (
              <span className="code-input__separator" aria-hidden="true">
                -
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

CodeInput.propTypes = {
  id: PropTypes.string.isRequired,
  length: PropTypes.number,
  mode: PropTypes.oneOf(['numeric', 'alphanumeric']),
  groupSize: PropTypes.number,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  describedBy: PropTypes.string
}

export default CodeInput
